import {
    eventBus,
    getGlobalVariables,
    isFailed,
    isShowLoader,
    selectError,
    selectMutableWidget,
    selectScenarioId,
    selectStatus,
    toPositiveNumber,
    TUseTranslation,
    useApp,
    useAppSelector,
    useAppStore,
    useOnline,
    useTranslation,
} from '@libs/core';
import { EStatus, SystemEventTypeEnum, TCommonData, TUseTriggerAction, TWidget } from '@libs/types';
import React, { useCallback, useEffect, useRef } from 'react';
import { probeConnectivity, TConnectivityVerdict } from '../common/connectivityProbe';
import { buildOfflineToast, OFFLINE_MESSAGE_FALLBACK, OFFLINE_MESSAGE_KEY } from '../common/Offline';
import OfflinePopup from '../common/OfflinePopup';
import { buildSlowConnectionToast } from '../common/SlowConnection';
import { useFirebaseConfig } from '../firebase/FlagsProvider';

export type TSlowRequestGuardFlagKeys = {
    toast?: string;
    popup?: string;
    cooldown?: string;
};

export type TSlowRequestGuardOptions = {
    triggerAction: TUseTriggerAction<TCommonData>['triggerAction'];
    isOnline?: boolean;
    isLoading?: boolean;
    isStatusFailed?: boolean;
    t?: TUseTranslation['t'];
    flagKeyPrefix?: string;
    flagKeys?: TSlowRequestGuardFlagKeys;
};

/**
 * Escalating feedback for a request that hangs.
 *
 * ```
 * request on the wire while isLoading === true
 *   → connectivity probe starts at toastDelay (default 1300 ms or from Firebase Remote Config)
 *   → toast "slow connection" (verdict-dependent) fires once the probe resolves
 *   → popup at popupDelay (default 6500 ms or from Firebase Remote Config)
 * request settles → probe aborted, timers cleared
 */
export const useSlowRequestGuard = ({
    triggerAction,
    isOnline: isOnlineProp,
    isLoading: isLoadingProp,
    isStatusFailed: isStatusFailedProp,
    t: tProp,
    flagKeyPrefix = 'ps3_consumer',
    flagKeys,
}: TSlowRequestGuardOptions) => {
    const { setPopup } = useApp();
    const online = useOnline();
    const isOnline = isOnlineProp ?? online;
    const storeLoader = useAppSelector(isShowLoader);
    const isLoading = isLoadingProp ?? storeLoader;
    const storeFailed = useAppSelector(isFailed);
    const isStatusFailed = isStatusFailedProp ?? storeFailed;
    const { t: storeT } = useTranslation();
    const t = tProp ?? storeT;
    const scenarioId = useAppSelector(selectScenarioId);
    const status = useAppSelector(selectStatus);
    const store = useAppStore();
    const flags = useFirebaseConfig();
    const isColdStartPopupShown = useRef(false);
    const { SLOW_CONNECTION_TOAST_DELAY_MS, SLOW_CONNECTION_POPUP_DELAY_MS, SLOW_CONNECTION_FEEDBACK_COOLDOWN_MS } = getGlobalVariables(
        'SLOW_CONNECTION_TOAST_DELAY_MS',
        'SLOW_CONNECTION_POPUP_DELAY_MS',
        'SLOW_CONNECTION_FEEDBACK_COOLDOWN_MS',
    );

    /**
     * Cold start without a connection.
     *
     * The root loader only dispatches `fetchScenario` when `navigator.onLine` is true, so an app
     * started offline never produces a rejected request: the store stays untouched and `isFailed`
     * is false. `EStatus.Idle` together with an empty `scenarioId` means no scenario was ever
     * loaded — the only state the player cannot return to once the first request has been sent
     * (pending switches the status to Loading / WidgetLoading right away).
     */
    const isColdStartOffline = status === EStatus.Idle && !scenarioId;

    const toastDelayKey = flagKeys?.toast ?? `${flagKeyPrefix}_slow_connection_notice_delay_ms`;
    const popupDelayKey = flagKeys?.popup ?? `${flagKeyPrefix}_slow_request_sheet_delay_ms`;
    const feedbackCooldownKey = flagKeys?.cooldown ?? `${flagKeyPrefix}_slow_connection_notice_cooldown_ms`;

    const toastDelay = toPositiveNumber(SLOW_CONNECTION_TOAST_DELAY_MS || flags[toastDelayKey], 1300);
    const popupDelay = toPositiveNumber(SLOW_CONNECTION_POPUP_DELAY_MS || flags[popupDelayKey], 6500);
    const feedbackCooldown = toPositiveNumber(SLOW_CONNECTION_FEEDBACK_COOLDOWN_MS || flags[feedbackCooldownKey], 10000);

    const handleRetry = useCallback(() => {
        if (!navigator.onLine) {
            // Still no connection: keep the popup open and give feedback with the offline toast.
            eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, buildOfflineToast(t(OFFLINE_MESSAGE_KEY, OFFLINE_MESSAGE_FALLBACK)));
            return;
        }

        const state = store.getState();
        const error = selectError(state);
        setPopup(null);

        if (!error?.actionForRepeat) {
            // Nothing to replay (e.g. the initial scenario request failed) — restart the player.
            window.location.reload();
            return;
        }

        const widget = error.widgetUid ? selectMutableWidget(state, error.widgetUid) : undefined;
        triggerAction(error.actionForRepeat, {
            widgetAction: widget as TWidget<TCommonData> | undefined,
            isShowLoader: true,
            // Without the originating widget a widget-scoped request cannot be rebuilt, so replay it as a scenario one.
            forceScenario: !widget,
        }).catch((e) => console.error('Offline retry failed:', e));
    }, [setPopup, store, t, triggerAction]);

    // Timestamps of the last toast / popup, each used for its own cooldown.
    const slowToastShownAt = useRef(0);
    const slowPopupShownAt = useRef(0);

    useEffect(() => {
        if (!isLoading || !isOnline) {
            return;
        }

        let isAborted = false;
        let probeVerdict: TConnectivityVerdict | null = null;
        const abortController = new AbortController();

        const probeTimer = setTimeout(() => {
            const probePromise = probeConnectivity(undefined, abortController.signal)
                .then((verdict) => {
                    probeVerdict = verdict;
                    return verdict;
                })
                .catch((e) => {
                    console.error('Connectivity probe failed:', e);
                    return 'unreachable' as TConnectivityVerdict;
                });

            const delayPromise = new Promise((resolve) => setTimeout(resolve, 200));

            Promise.all([probePromise, delayPromise]).then(([verdict]) => {
                if (isAborted) {
                    return;
                }
                const now = Date.now();
                if (now - slowToastShownAt.current >= feedbackCooldown) {
                    slowToastShownAt.current = now;
                    const messageKey = verdict === 'online' ? 'internet.slow.ps.connection.message' : 'internet.slow.connection.message';
                    eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, buildSlowConnectionToast(t(messageKey)));
                }
            });
        }, toastDelay);

        const popupTimer = setTimeout(() => {
            const now = Date.now();
            if (now - slowPopupShownAt.current >= feedbackCooldown) {
                slowPopupShownAt.current = now;
                const isSlowService = probeVerdict === 'online';
                setPopup(<OfflinePopup variant={isSlowService ? 'slowService' : 'offline'} onRetry={isSlowService ? () => setPopup(null) : handleRetry} />);
            }
        }, popupDelay);

        return () => {
            isAborted = true;
            clearTimeout(probeTimer);
            clearTimeout(popupTimer);
            abortController.abort();
        };
    }, [feedbackCooldown, isLoading, isOnline, handleRetry, popupDelay, setPopup, t, toastDelay]);

    useEffect(() => {
        if (!isOnline && (isStatusFailed || isColdStartOffline)) {
            isColdStartPopupShown.current = isColdStartOffline;
            setPopup(<OfflinePopup variant="offline" onRetry={handleRetry} />);
        }
    }, [handleRetry, isColdStartOffline, isOnline, isStatusFailed, setPopup]);

    useEffect(() => {
        // The connection is back after a cold start: nothing was ever rendered behind the popup and the
        // loader will not run again on its own, so restart the player instead of leaving an empty screen.
        if (isOnline && isColdStartPopupShown.current && isColdStartOffline) {
            isColdStartPopupShown.current = false;
            setPopup(null);
            window.location.reload();
        }
    }, [isColdStartOffline, isOnline, setPopup]);
};
